import type { AuditLogChange, ShiftAuditLog } from "@/hooks/useShiftAuditLogs"

export type AuditScope = "all" | "shifts" | "timesheets"

const VISIBLE_AUDIT_FIELDS = {
  shifts: new Set([
    "area",
    "awol",
    "cancelled_reason",
    "cancelled_time",
    "depot_id",
    "employer",
    "end_time",
    "location",
    "minutes_late",
    "notes",
    "notifications",
    "person",
    "preassigned_route",
    "start_time",
    "status",
    "type",
    "vehicle_reg",
  ]),
  timesheets: new Set([
    "auto_clock_out",
    "break",
    "clocked_in_time",
    "clocked_out_time",
    "end_latitude",
    "end_longitude",
    "latitude",
    "longitude",
    "person",
    "shift_id",
    "status",
  ]),
} as const

export const isAuditScope = (value: string): value is AuditScope =>
  value === "all" || value === "shifts" || value === "timesheets"

export const formatFieldLabel = (field: string) =>
  field
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")

export const getEntityLabel = (tableName: string) => {
  if (tableName === "timesheets") return "Timesheet"
  if (tableName === "shifts") return "Block"
  return "Record"
}

export const getEntityBadgeClassName = (tableName: string) => {
  if (tableName === "timesheets") {
    return "border-amber-200 bg-amber-50 text-amber-700"
  }

  if (tableName === "shifts") {
    return "border-sky-200 bg-sky-50 text-sky-700"
  }

  return "border-palette-jungleGreen200 bg-palette-jungleGreen000 text-palette-jungleGreen700"
}

export const getVisibleAuditChanges = (entry: ShiftAuditLog): Array<[string, AuditLogChange]> => {
  const visibleFields = VISIBLE_AUDIT_FIELDS[entry.table_name as keyof typeof VISIBLE_AUDIT_FIELDS]
  const changes = Object.entries(entry.changes ?? {})

  if (!visibleFields) {
    return changes
  }

  return changes.filter(([field]) => visibleFields.has(field))
}

export const getAuditEmptyStateMessage = (scope: AuditScope) => {
  if (scope === "shifts") {
    return "No block changes recorded for this block yet."
  }

  if (scope === "timesheets") {
    return "No timesheet changes recorded for this block yet."
  }

  return "No changes recorded for this block yet."
}
